import {useCallback, useEffect, useState} from 'react';
import {createTeamMember, deleteTeamMember, getTeamMembers, updateTeamMember,} from '../services/teamMemberService';
import {FeedbackTeam, TeamMember} from '../types/feedback';

export function useTeamMembers(filterByTeam?: FeedbackTeam) {
    const [members, setMembers] = useState<TeamMember[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchMembers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getTeamMembers(filterByTeam);
            setMembers(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch team members');
        } finally {
            setLoading(false);
        }
    }, [filterByTeam]);

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    const addMember = useCallback(
        async (data: Omit<TeamMember, 'id' | 'createdAt' | 'updatedAt'>) => {
            setError(null);
            try {
                await createTeamMember(data);
                await fetchMembers();
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to add team member');
                throw err;
            }
        },
        [fetchMembers]
    );

    const editMember = useCallback(
        async (id: string, updates: Partial<Pick<TeamMember,
            'name' | 'team' | 'userGroupId' | 'userGroup' |
            'canBeAssigned' | 'hasSupportAccess' | 'hasImmersion'>>) => {
            setError(null);
            try {
                await updateTeamMember(id, updates);
                await fetchMembers();
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to update team member');
                throw err;
            }
        },
        [fetchMembers]
    );

    const removeMember = useCallback(
        async (id: string) => {
            setError(null);
            try {
                await deleteTeamMember(id);
                await fetchMembers();
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to delete team member');
                throw err;
            }
        },
        [fetchMembers]
    );

    return {
        members,
        loading,
        error,
        refresh: fetchMembers,
        addMember,
        editMember,
        removeMember,
    };
}
